import { PluginListenerHandle } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotificationSchema,
  RegistrationError,
  Token,
} from "@capacitor/push-notifications";

import { isIosPlatform } from "utils/platform.helpers";

import { AndroidPushNotificationAdapter } from "../AndroidPushNotificationAdapter/AndroidPushNotificationAdapter";
import { IOSPushNotificationAdapter } from "../IOSPushNotificationAdapter/IOSPushNotificationAdapter";
import { IPushNotificationAdapter } from "../IPushNotificationAdapter";
import {
  PushNotificationError,
  PushNotificationErrorType,
} from "../PushNotificationError/PushNotificationError";

export class PushNotificationService {
  private readonly adapter: IPushNotificationAdapter;

  constructor(adapter: IPushNotificationAdapter) {
    this.adapter = adapter;
  }

  public async register(): Promise<void> {
    return this.adapter.register();
  }

  public async requestPermission(): Promise<boolean> {
    try {
      await this.adapter.requestPermission();
      return true;
    } catch (error) {
      if (
        error instanceof PushNotificationError &&
        error.type === PushNotificationErrorType.PERMISSION_ERROR
      ) {
        console.warn(
          "[RMD]: Push notification permission not granted by user",
          error
        );
      } else {
        console.error(
          "[RMD]: Failed to request push notification permission",
          error
        );
      }
      return false;
    }
  }

  public openAppSettings(): void {
    this.adapter.openAppSettings();
  }

  public onRegistrationError(
    callback: (err: RegistrationError) => void
  ): Promise<PluginListenerHandle> {
    return this.adapter.onRegistrationError(callback);
  }

  public onTokenReceived(
    callback: (token: Token) => void
  ): Promise<PluginListenerHandle> {
    return this.adapter.onTokenReceived(callback);
  }

  public onNotificationReceived(
    callback: (notification: PushNotificationSchema) => void
  ): Promise<PluginListenerHandle> {
    return this.adapter.onNotificationReceived(callback);
  }

  public onNotificationActionPerformed(
    callback: (notification: ActionPerformed) => void
  ): Promise<PluginListenerHandle> {
    return this.adapter.onActionPerformed(callback);
  }
}

function getAdapter() {
  return isIosPlatform()
    ? new IOSPushNotificationAdapter()
    : new AndroidPushNotificationAdapter();
}

const pushNotificationAdapter = getAdapter();

export const pushNotificationService = new PushNotificationService(
  pushNotificationAdapter
);
